import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Paper,
  TextField,
  Button,
  Fade,
  Backdrop,
  Tooltip,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import { useSelector } from "react-redux";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import { BACKGROUND_COLORS, BORDER_COLORS, TEXT_COLORS, BRAND_COLORS } from "../../style/colors.js";
import { translateText } from "../../translations/translationService";
import AppDialog from "../Common/ui/AppDialog";
import AppButton from "../Common/ui/AppButton";

export default function ComponentObservaciones({
  onSave,
  initialComment = "",
  maxLength = 500,
  required = false,
  title = "Escribir Observación",
}) {
  const [modalVisible, setModalVisible] = useState(false);
  const [comment, setComment] = useState(initialComment);
  const [error, setError] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const lang = useSelector((state) => state.lang.value);

  // Confirmación "tienes cambios" (reemplaza window.confirm)
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const confirmResolverRef = useRef(null);

  const askConfirm = (message) => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmMessage(message);
      setConfirmVisible(true);
    });
  };

  // Detectar cambios no guardados
  useEffect(() => {
    setHasUnsavedChanges(comment !== initialComment);
  }, [comment, initialComment]);

  const handleOpenModal = () => {
    setModalVisible(true);
    setComment(initialComment); // Restaurar comentario inicial
    setError("");
  };

  const handleCloseModal = async () => {
    if (hasUnsavedChanges) {
      const confirmClose = await askConfirm(
        translateText(
          "Tienes cambios sin guardar. ¿Estás seguro de cerrar?",
          lang
        )
      );
      if (!confirmClose) return;
    }

    setModalVisible(false);
    setComment(initialComment);
    setError("");
    setHasUnsavedChanges(false);
  };

  const handleCommentChange = (e) => {
    const value = e.target.value;
    setComment(value);

    // Validaciones en tiempo real
    if (required && value.trim().length === 0) {
      setError(translateText("Este campo es obligatorio", lang));
    } else if (value.length > maxLength) {
      setError(translateText(`Máximo ${maxLength} caracteres`, lang));
    } else {
      setError("");
    }
  };

  const handleSaveComment = () => {
    // Validación final
    const trimmedComment = comment.trim();

    if (required && trimmedComment.length === 0) {
      setError(translateText("Este campo es obligatorio", lang));
      return;
    }

    if (comment.length > maxLength) {
      setError(translateText(`Máximo ${maxLength} caracteres`, lang));
      return;
    }

    if (onSave) {
      onSave(trimmedComment);
    }

    setModalVisible(false);
    setError("");
    setHasUnsavedChanges(false);
  };

  const handleClearComment = () => {
    setComment("");
    setError("");
  };

  const getCharacterCount = () => comment.length;
  const getRemainingChars = () => maxLength - comment.length;
  const isOverLimit = () => comment.length > maxLength;
  const hasContent = () => comment.trim().length > 0;

  return (
    <Box>
      {/* Botón de observaciones mejorado */}
      <Tooltip
        title={translateText("Añadir observación", lang)}
        placement='top'
        arrow>
        <IconButton
          onClick={handleOpenModal}
          sx={{
            color: BRAND_COLORS.PRIMARY,
            backgroundColor: hasContent()
              ? "rgba(0, 0, 0, 0.08)"
              : "transparent",
            border: hasContent()
              ? "2px solid rgba(0, 0, 0, 0.12)"
              : "2px solid transparent",
            borderRadius: 2,
            padding: 1.5,
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              transform: "scale(1.05)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}>
          <EditNoteIcon fontSize='large' />
        </IconButton>
      </Tooltip>

      {/* Indicador de contenido */}
      {hasContent() && (
        <Chip
          size='small'
          label={`${comment.trim().split(/\s+/).length} ${translateText(
            "palabras",
            lang
          )}`}
          sx={{
            position: "absolute",
            top: -8,
            right: -8,
            backgroundColor: BRAND_COLORS.PRIMARY,
            color: "white",
            fontSize: "0.7rem",
            height: 20,
          }}
        />
      )}

      {/* Modal mejorado con animaciones */}
      <Modal
        open={modalVisible}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          // Tinte del fondo para que encaje con la paleta de la app
          sx: { backgroundColor: "rgba(49, 79, 91, 0.55)" },
        }}>
        <Fade in={modalVisible}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "95%", sm: "85%", md: "60%", lg: "50%" },
              maxWidth: "600px",
              bgcolor: "background.paper",
              borderRadius: 3,
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              p: 0,
              overflow: "hidden",
            }}>
            {/* Header mejorado con fondo gris */}
            <Box
              sx={{
                // Header del modal acorde a la paleta de la app
                background: `linear-gradient(135deg, ${BRAND_COLORS.PRIMARY}, ${BRAND_COLORS.PRIMARY_DARK})`,
                color: "white",
                p: 3,
                position: "relative",
              }}>
              <Typography
                variant='h5'
                component='h2'
                sx={{ fontWeight: 600, pr: 6 }}>
                {translateText(title, lang)}
              </Typography>
              <Typography variant='body2' sx={{ opacity: 0.9, mt: 0.5 }}>
                {translateText(
                  "Comparte tus observaciones y comentarios",
                  lang
                )}
              </Typography>

              <IconButton
                onClick={handleCloseModal}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
                }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Contenido del modal */}
            <Box sx={{ p: 3 }}>
              {/* Campo de texto mejorado */}
              <TextField
                fullWidth
                multiline
                rows={6}
                variant='outlined'
                value={comment}
                onChange={handleCommentChange}
                placeholder={translateText(
                  "Escribe tu observación aquí...",
                  lang
                )}
                error={!!error}
                helperText={error}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: BACKGROUND_COLORS.SECONDARY,
                    "&:hover fieldset": {
                      borderColor: BRAND_COLORS.PRIMARY,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: BRAND_COLORS.PRIMARY,
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "1rem",
                    lineHeight: 1.6,
                  },
                }}
              />

              {/* Contador de caracteres */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1,
                  mb: 2,
                }}>
                <Typography
                  variant='caption'
                  color={isOverLimit() ? "error" : "text.secondary"}>
                  {getCharacterCount()}/{maxLength}{" "}
                  {translateText("caracteres", lang)}
                </Typography>

                {getRemainingChars() <= 50 && getRemainingChars() > 0 && (
                  <Typography variant='caption' color='warning.main'>
                    {translateText("Quedan", lang)} {getRemainingChars()}{" "}
                    {translateText("caracteres", lang)}
                  </Typography>
                )}
              </Box>

              {/* Alerta de validación */}
              {required && comment.trim().length === 0 && (
                <Alert severity='info' sx={{ mb: 2 }}>
                  {translateText("Este campo es obligatorio", lang)}
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Botones de acción mejorados */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}>
                <Box>
                  {hasContent() && (
                    <Button
                      variant='text'
                      color='error'
                      startIcon={<DeleteIcon />}
                      onClick={handleClearComment}
                      sx={{ mr: 1 }}>
                      {translateText("Limpiar", lang)}
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant='outlined'
                    onClick={handleCloseModal}
                    sx={{
                      borderColor: "#ddd",
                      color: "#666",
                      "&:hover": {
                        borderColor: "#bbb",
                        backgroundColor: BACKGROUND_COLORS.SECONDARY,
                      },
                    }}>
                    {translateText("Cancelar", lang)}
                  </Button>

                  <Button
                    variant='contained'
                    startIcon={<SaveIcon />}
                    onClick={handleSaveComment}
                    disabled={
                      !!error || (required && comment.trim().length === 0)
                    }
                    sx={{
                      backgroundColor: BRAND_COLORS.PRIMARY,
                      "&:hover": {
                        backgroundColor: BRAND_COLORS.PRIMARY,
                        filter: "brightness(0.9)",
                      },
                      "&:disabled": {
                        backgroundColor: "#ccc",
                      },
                    }}>
                    {translateText("Guardar", lang)}
                  </Button>
                </Box>
              </Box>

              {/* Indicador de cambios no guardados */}
              {hasUnsavedChanges && (
                <Typography
                  variant='caption'
                  color='warning.main'
                  sx={{ display: "block", textAlign: "center", mt: 1 }}>
                  ● {translateText("Cambios sin guardar", lang)}
                </Typography>
              )}
            </Box>
          </Paper>
        </Fade>
      </Modal>

      <AppDialog
        open={confirmVisible}
        onClose={() => {
          setConfirmVisible(false);
          confirmResolverRef.current?.(false);
        }}
        title='Confirmación'
        maxWidth='xs'
        actions={
          <>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(false);
              }}
              variantType='secondary'>
              Cancelar
            </AppButton>
            <AppButton
              onClick={() => {
                setConfirmVisible(false);
                confirmResolverRef.current?.(true);
              }}>
              {translateText("Cerrar", lang) || "Aceptar"}
            </AppButton>
          </>
        }>
        <Typography>{confirmMessage}</Typography>
      </AppDialog>
    </Box>
  );
}
